// services/gridService.js

const API_URL = 'https://api.subtidal.com';

/**
 * Fetches all grids
 *
 * @param {string} token - Authentication token
 * @returns {Promise<Array>} - Array of grid objects
 */
export const getAllGrids = async (token) => {
  try {
    const response = await fetch(`${API_URL}/grids/`, {
      headers: {
        "Authorization": `Token ${token}`,
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch grids: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching grids:', error);
    throw error;
  }
};

/**
 * Fetches a specific grid by ID
 *
 * @param {string|number} gridId - The ID of the grid
 * @param {string} token - Authentication token
 * @returns {Promise<Object>} - Grid data
 */
export const getGridById = async (gridId, token) => {
  try {
    const response = await fetch(`${API_URL}/grids/${gridId}/`, {
      headers: {
        "Authorization": `Token ${token}`,
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch grid: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error(`Error fetching grid ${gridId}:`, error);
    throw error;
  }
};

/**
 * Creates a new grid
 *
 * @param {Object} gridData - Grid data to create
 * @param {string} token - Authentication token
 * @returns {Promise<Object>} - Created grid data
 */
export const createGrid = async (gridData, token) => {
  try {
    const response = await fetch(`${API_URL}/grids/`, {
      method: 'POST',
      headers: {
        "Authorization": `Token ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(gridData)
    });

    if (!response.ok) {
      throw new Error(`Failed to create grid: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error creating grid:', error);
    throw error;
  }
};

/**
 * Updates an existing grid
 *
 * @param {string|number} gridId - The ID of the grid to update
 * @param {Object} gridData - Updated grid data
 * @param {string} token - Authentication token
 * @returns {Promise<Object>} - Updated grid data
 */
export const updateGrid = async (gridId, gridData, token) => {
  try {
    const response = await fetch(`${API_URL}/grids/${gridId}/`, {
      method: 'PATCH',
      headers: {
        "Authorization": `Token ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(gridData)
    });

    if (!response.ok) {
      throw new Error(`Failed to update grid: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error(`Error updating grid ${gridId}:`, error);
    throw error;
  }
};

/**
 * Deletes a grid
 *
 * @param {string|number} gridId - The ID of the grid to delete
 * @param {string} token - Authentication token
 * @returns {Promise<void>}
 */
export const deleteGrid = async (gridId, token) => {
  try {
    const response = await fetch(`${API_URL}/grids/${gridId}/`, {
      method: 'DELETE',
      headers: {
        "Authorization": `Token ${token}`,
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to delete grid: ${response.status}`);
    }

    return true;
  } catch (error) {
    console.error(`Error deleting grid ${gridId}:`, error);
    throw error;
  }
};

/**
 * Fetches grid statistics (summary data)
 *
 * @param {string|number} gridId - The ID of the grid
 * @param {string} token - Authentication token
 * @returns {Promise<Object>} - Grid statistics
 */
export const getGridStatistics = async (gridId, token) => {
  try {
    const response = await fetch(`${API_URL}/grids/${gridId}/statistics/`, {
      headers: {
        "Authorization": `Token ${token}`,
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch grid statistics: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error(`Error fetching statistics for grid ${gridId}:`, error);
    throw error;
  }
};